import React, { createContext, useContext, useState, useEffect } from 'react';
import { UserProfile, PublicTenantInfo, LoginCredentials, RegisterTenantPayload } from '@saas/shared-types';
import { api } from '../services/api';
import { useTenant } from './TenantContext';

interface AuthContextValue {
  user: UserProfile | null;
  tenant: PublicTenantInfo | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (credentials: LoginCredentials) => Promise<void>;
  logout: () => void;
  registerTenant: (payload: RegisterTenantPayload) => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { switchTenant } = useTenant();
  const [user, setUser] = useState<UserProfile | null>(null);
  const [tenant, setTenant] = useState<PublicTenantInfo | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  // Initialize session on mount
  useEffect(() => {
    const initAuth = async () => {
      const token = api.getAccessToken();
      if (!token) {
        setIsLoading(false);
        return;
      }

      try {
        const data = await api.getMe();
        setUser(data.user);
        setTenant(data.tenant);
      } catch (err) {
        console.warn('[AuthContext] Session expired or invalid:', err);
        api.setAccessToken(null);
        setUser(null);
        setTenant(null);
      } finally {
        setIsLoading(false);
      }
    };

    initAuth();
  }, []);

  const login = async (credentials: LoginCredentials) => {
    setIsLoading(true);
    try {
      const response = await api.login(credentials);
      setUser(response.user);
      setTenant(response.tenant);
    } finally {
      setIsLoading(false);
    }
  };

  const logout = () => {
    api.setAccessToken(null);
    localStorage.removeItem('saas_refresh_token');
    setUser(null);
    setTenant(null);
  };

  const registerTenant = async (payload: RegisterTenantPayload) => {
    setIsLoading(true);
    try {
      await api.registerTenant(payload);
      // Automatically switch to the newly registered tenant
      await switchTenant(payload.subdomain);
      // Log the user into their newly seeded account
      await login({
        email: payload.adminEmail,
        password: payload.adminPassword,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        tenant,
        isAuthenticated: !!user,
        isLoading,
        login,
        logout,
        registerTenant,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextValue => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
